"use client"

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, CheckCircle2, User, Mail, Phone, MapPin, Building2, Briefcase, Fingerprint, Lock, ShieldAlert } from "lucide-react";
import { Turnstile } from '@marsidev/react-turnstile';

export default function InstitutionalPartnershipsPage() {
  const [formData, setFormData] = useState({
    investorType: "Organization",
    fullName: "",
    organization: "",
    email: "",
    mobile: "",
    address: "",
    message: "",
  });

  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const [isFormValid, setIsFormValid] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");

  // Biometric Vault State
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [hasCheckedSession, setHasCheckedSession] = useState(false);
  const [authStatus, setAuthStatus] = useState<"idle" | "scanning" | "denied" | "success">("idle");

  useEffect(() => {
    if (typeof window !== "undefined") {
      const unlocked = sessionStorage.getItem('wpp_vault_unlocked');
      if (unlocked === 'true') {
        setIsUnlocked(true);
      }
      setHasCheckedSession(true);
    }
  }, []);
  
  // 2FA Email Fallback State
  const [fallbackStep, setFallbackStep] = useState<"email" | "loading" | "pin">("email");
  const [authEmail, setAuthEmail] = useState("");
  const [enteredPin, setEnteredPin] = useState("");
  const [generatedPin, setGeneratedPin] = useState("");

  const handleBiometricAuth = async () => {
    setAuthStatus("scanning");
    
    try {
      if (window.PublicKeyCredential && await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()) {
        const challenge = new Uint8Array(32);
        window.crypto.getRandomValues(challenge);
        
        await navigator.credentials.create({
          publicKey: {
            challenge: challenge,
            rp: { name: "Project Vanguard Security", id: window.location.hostname },
            user: {
              id: new Uint8Array(16),
              name: "investor@wpp.com",
              displayName: "Institutional Access"
            },
            pubKeyCredParams: [{ type: "public-key", alg: -7 }],
            authenticatorSelection: { userVerification: "required", authenticatorAttachment: "platform" },
            timeout: 60000,
            attestation: "none"
          }
        });
        
        setAuthStatus("success");
        sessionStorage.setItem('wpp_vault_unlocked', 'true');
        setTimeout(() => setIsUnlocked(true), 1500);
      } else {
        setAuthStatus("denied");
        setTimeout(() => setAuthStatus("idle"), 3000);
      }
    } catch (e) {
      console.error(e);
      setAuthStatus("denied");
      setTimeout(() => setAuthStatus("idle"), 3000);
    }
  };

  const handleRequestPin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!authEmail) return;

    setFallbackStep("loading");
    // Generate a secure 6-digit PIN
    const newPin = Math.floor(100000 + Math.random() * 900000).toString();
    setGeneratedPin(newPin);

    try {
      const res = await fetch("/api/vault-auth", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: authEmail, pin: newPin })
      });

      if (res.ok) {
        setFallbackStep("pin");
      } else {
        setAuthStatus("denied");
        setFallbackStep("email");
        setTimeout(() => setAuthStatus("idle"), 3000);
      }
    } catch (err) {
      console.error(err);
      setAuthStatus("denied");
      setFallbackStep("email");
      setTimeout(() => setAuthStatus("idle"), 3000);
    }
  };

  const handlePinSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (enteredPin === generatedPin && generatedPin !== "") {
      setAuthStatus("success");
      sessionStorage.setItem('wpp_vault_unlocked', 'true');
      setTimeout(() => setIsUnlocked(true), 1500);
    } else {
      setAuthStatus("denied");
      setEnteredPin("");
      setTimeout(() => setAuthStatus("idle"), 3000);
    }
  };

  // 🏛️ ELITE VALIDATION LOGIC: Strictly compulsory check
  useEffect(() => {
    const requiredFields = [formData.fullName, formData.email, formData.mobile, formData.address, formData.message];
    if (formData.investorType === "Organization") {
      requiredFields.push(formData.organization);
    }
    const isValid = requiredFields.every((val) => val.trim().length > 0) && !!turnstileToken;
    setIsFormValid(isValid);
  }, [formData, turnstileToken]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) return;

    setStatus("loading");
    setErrorMessage(""); // clear previous errors
    try {
      const res = await fetch("/api/institutional", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...formData, turnstileToken }),
      });

      if (res.ok) {
        setStatus("success");
        setFormData({
          investorType: "Organization",
          fullName: "",
          organization: "",
          email: "",
          mobile: "",
          address: "",
          message: "",
        });
        setTurnstileToken(""); // Reset token on success
      } else {
        const errorData = await res.json().catch(() => null);
        setErrorMessage(errorData?.message || "Submission failed. Please try again.");
        setStatus("error");
      }
    } catch (err) {
      setErrorMessage("Network error. Please check your connection and try again.");
      setStatus("error");
    }
  };

  const inputClasses = "w-full bg-white/5 border border-white/10 px-5 py-4 text-sm font-serif outline-none focus:border-brand focus:ring-1 focus:ring-brand transition-all text-white placeholder:text-zinc-600";

  if (!hasCheckedSession) {
    return <div className="fixed inset-0 bg-[#020202] z-[9999] flex items-center justify-center">
      <div className="w-6 h-6 border-2 border-brand border-t-transparent rounded-full animate-spin" />
    </div>;
  }

  if (!isUnlocked) {
    return (
      <div className="fixed inset-0 bg-[#020202] z-[9999] flex flex-col items-center justify-center font-mono overflow-hidden">
        <style dangerouslySetInnerHTML={{
          __html: `
          input:-webkit-autofill,
          input:-webkit-autofill:hover, 
          input:-webkit-autofill:focus, 
          input:-webkit-autofill:active {
              -webkit-box-shadow: 0 0 0 1000px #020202 inset !important;
              -webkit-text-fill-color: white !important;
              transition: background-color 5000s ease-in-out 0s;
          }
        `}} />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:32px_32px]" />
        
        <div className="relative z-10 flex flex-col items-center max-w-md w-full px-6 text-center">
          <div className="w-16 h-16 bg-black border border-red-500/30 flex items-center justify-center rounded-xl mb-8 relative shadow-[0_0_30px_rgba(255,0,0,0.15)]">
            <ShieldAlert className="w-8 h-8 text-red-500" />
            <div className="absolute inset-0 bg-red-500/10 animate-pulse rounded-xl" />
          </div>

          <h1 className="text-white font-black text-xl md:text-2xl tracking-tight mb-2">RESTRICTED AREA</h1>
          <p className="text-red-500 text-xs font-bold tracking-[0.3em] uppercase mb-12">Project Vanguard Vault</p>

          <AnimatePresence mode="wait">
            {authStatus === "denied" && (
              <motion.div key="denied" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="text-red-500 font-bold mb-8 animate-pulse text-sm">
                ACCESS DENIED. VERIFICATION FAILED.
              </motion.div>
            )}
            
            {authStatus === "success" && (
              <motion.div key="success" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-green-500 font-bold mb-8 text-sm flex flex-col items-center gap-4">
                <CheckCircle2 className="w-12 h-12" />
                IDENTITY VERIFIED. UNLOCKING VAULT...
              </motion.div>
            )}

            {authStatus !== "success" && (
              <div className="w-full space-y-6">
                <AnimatePresence mode="wait">
                  {fallbackStep === "email" && (
                    <motion.form key="email-form" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} onSubmit={handleRequestPin} className="w-full space-y-4">
                      <div className="space-y-2">
                        <label className="text-[10px] text-zinc-500 uppercase tracking-widest">Corporate Email Address</label>
                        <div className="relative">
                          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
                          <input
                            type="email" required autoFocus
                            placeholder="Enter email to receive PIN"
                            className="w-full bg-black/50 border border-white/10 px-5 py-4 pl-12 text-sm text-white outline-none focus:border-brand focus:ring-1 focus:ring-brand transition-all font-mono"
                            value={authEmail} onChange={(e) => setAuthEmail(e.target.value)}
                          />
                        </div>
                      </div>
                      <button type="submit" disabled={!authEmail || authStatus === "scanning"} className="w-full bg-brand hover:bg-brand-light py-4 text-[10px] font-black uppercase tracking-[0.3em] flex items-center justify-center transition-all disabled:opacity-30 text-white" style={{ clipPath: 'polygon(0 0, calc(100% - 10px) 0, 100% 10px, 100% 100%, 10px 100%, 0 calc(100% - 10px))' }}>
                        REQUEST ACCESS PIN
                      </button>
                    </motion.form>
                  )}

                  {fallbackStep === "loading" && (
                    <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="py-8 flex flex-col items-center justify-center">
                      <span className="w-6 h-6 border-2 border-brand border-t-transparent rounded-full animate-spin mb-4" />
                      <p className="text-xs text-brand font-bold tracking-widest uppercase">TRANSMITTING SECURE PIN...</p>
                    </motion.div>
                  )}

                  {fallbackStep === "pin" && (
                    <motion.form key="pin-form" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} onSubmit={handlePinSubmit} className="w-full space-y-4">
                      <div className="space-y-2">
                        <label className="text-[10px] text-zinc-500 uppercase tracking-widest">Enter Access PIN Sent to {authEmail}</label>
                        <div className="relative">
                          <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
                          <input
                            type="password" autoFocus required maxLength={6}
                            className="w-full bg-black/50 border border-brand/50 px-5 py-4 pl-12 text-center tracking-[0.5em] text-xl text-white outline-none focus:border-brand focus:ring-1 focus:ring-brand transition-all font-mono shadow-[0_0_15px_rgba(255,0,0,0.1)]"
                            value={enteredPin} onChange={(e) => setEnteredPin(e.target.value.replace(/\D/g, ''))}
                          />
                        </div>
                      </div>
                      <button type="submit" disabled={enteredPin.length !== 6} className="w-full bg-brand hover:bg-brand-light py-4 text-[10px] font-black uppercase tracking-[0.3em] flex items-center justify-center transition-all disabled:opacity-30 text-white" style={{ clipPath: 'polygon(0 0, calc(100% - 10px) 0, 100% 10px, 100% 100%, 10px 100%, 0 calc(100% - 10px))' }}>
                        AUTHORIZE VAULT
                      </button>
                    </motion.form>
                  )}
                </AnimatePresence>

                {fallbackStep === "email" && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6 pt-2">
                    <div className="flex items-center gap-4 w-full opacity-30">
                      <div className="h-px bg-white flex-1" />
                      <span className="text-[10px] uppercase tracking-widest font-bold text-white">OR</span>
                      <div className="h-px bg-white flex-1" />
                    </div>

                    <button
                      type="button"
                      onClick={handleBiometricAuth}
                      disabled={authStatus === "scanning"}
                      className={`w-full py-4 px-6 flex items-center justify-center gap-3 border transition-all ${
                        authStatus === "scanning" 
                          ? "bg-brand/10 border-brand/50 text-brand" 
                          : "bg-black/50 border-white/10 hover:border-brand hover:bg-brand/5 text-zinc-400 hover:text-white"
                      }`}
                    >
                      <Fingerprint className={`w-5 h-5 ${authStatus === "scanning" ? "animate-pulse text-brand" : ""}`} />
                      <span className="text-[10px] font-bold tracking-widest uppercase">
                        {authStatus === "scanning" ? "AWAITING SCAN..." : "USE DEVICE BIOMETRICS"}
                      </span>
                    </button>
                  </motion.div>
                )}
              </div>
            )}
          </AnimatePresence>

          <p className="mt-12 text-[9px] text-zinc-600 uppercase tracking-widest leading-relaxed max-w-xs mx-auto">
            This terminal is protected by Vanguard Zero-Trust protocols. Unauthorized access will be logged and reported.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#020202] text-white pt-32 pb-20 relative overflow-hidden">
      <style dangerouslySetInnerHTML={{
        __html: `
        input:-webkit-autofill,
        input:-webkit-autofill:hover, 
        input:-webkit-autofill:focus, 
        input:-webkit-autofill:active,
        textarea:-webkit-autofill,
        textarea:-webkit-autofill:hover,
        textarea:-webkit-autofill:focus,
        textarea:-webkit-autofill:active {
            -webkit-box-shadow: 0 0 0 1000px #020202 inset !important;
            -webkit-text-fill-color: white !important;
            transition: background-color 5000s ease-in-out 0s;
        }
      `}} />
      
      {/* Premium Background FX */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:32px_32px]" />
      <div className="absolute top-1/4 -left-1/4 w-[800px] h-[800px] bg-brand/10 rounded-full blur-[150px] pointer-events-none" />

      <div className="container mx-auto px-4 md:px-8 max-w-[1200px] relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">

          {/* Left: The Corporate Manifesto */}
          <div className="lg:col-span-5 space-y-12">
            <div className="space-y-6 relative">
              <div className="flex items-center gap-3">
                <span className="w-2 h-2 rounded-full bg-brand animate-pulse" />
                <span className="text-brand font-black uppercase tracking-[0.4em] text-[10px]">Project Vanguard</span>
              </div>
              <h1 className="brand-logo-font text-5xl md:text-7xl leading-[0.9] text-white tracking-tight">
                Capital & <br/>Alliances.
              </h1>
              <p className="font-serif italic text-xl md:text-2xl text-zinc-400 leading-tight">
                "Inviting visionary capital and elite brands to power the next generation of global intelligence."
              </p>
            </div>

            <div className="space-y-8 pt-8 border-t border-white/10 text-sm md:text-base font-serif text-zinc-300 leading-relaxed">
              <p>World Prime Post is the definitive record of the global shift. As we architect a high-fidelity intelligence terminal for the 21st century, we seek alignment with institutional investors, family offices, and leading brands committed to independent, high-authority journalism.</p>

              <div className="grid grid-cols-1 gap-4 pt-4">
                {[
                  "Global Capital Infusion & Equity",
                  "Premium Corporate Sponsorships",
                  "Strategic Technology Alliances",
                  "Philanthropic Grants"
                ].map((item, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.2 + (idx * 0.1) }}
                    className="flex items-center gap-4 group p-4 border border-white/5 bg-white/[0.02] hover:bg-white/[0.05] hover:border-brand/30 transition-all"
                  >
                    <div className="w-2 h-2 bg-brand/50 group-hover:bg-brand transition-colors shadow-[0_0_8px_rgba(255,0,0,0.5)]" />
                    <span className="text-[11px] font-black uppercase tracking-[0.2em] text-zinc-300 group-hover:text-white transition-colors">{item}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>

          {/* Right: The Secure Investment Vault */}
          <div className="lg:col-span-7 relative mt-8 lg:mt-0">
            {/* Tech Vault Design */}
            <div className="absolute inset-0 bg-zinc-950/80 backdrop-blur-3xl border border-white/10 shadow-2xl" 
                 style={{ clipPath: 'polygon(0 0, calc(100% - 30px) 0, 100% 30px, 100% 100%, 30px 100%, 0 calc(100% - 30px))' }} />
            
            {/* Edge Accents */}
            <div className="absolute top-0 right-[30px] w-8 h-px bg-brand" />
            <div className="absolute bottom-0 left-[30px] w-8 h-px bg-brand" />

            <div className="relative p-8 md:p-12 z-10">
              <div className="flex items-center justify-between mb-8 border-b border-white/10 pb-4">
                <h3 className="text-sm font-mono text-zinc-400 uppercase tracking-widest">Secure Transmission</h3>
                <div className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                  <span className="text-[9px] text-green-500 font-mono uppercase tracking-widest">E2E Encrypted</span>
                </div>
              </div>

              <AnimatePresence mode="wait">
                {status === "success" ? (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="h-full flex flex-col items-center justify-center text-center space-y-6 py-20"
                  >
                    <div className="w-20 h-20 bg-brand/10 flex items-center justify-center border border-brand/40" style={{ clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)' }}>
                      <CheckCircle2 className="w-10 h-10 text-brand" />
                    </div>
                    <h3 className="text-3xl font-serif font-bold text-white">Proposal Registered.</h3>
                    <p className="text-sm text-zinc-400 font-mono max-w-[320px] leading-relaxed">Our executive board will review your structural proposal and initiate contact through secure channels.</p>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    onSubmit={handleSubmit}
                    className="space-y-8 relative z-10"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >

                    {/* Invisible Cloudflare Turnstile */}
                    <div className="w-full flex justify-center hidden">
                      <Turnstile
                        siteKey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY!}
                        onSuccess={(token) => setTurnstileToken(token)}
                        options={{ theme: 'dark' }}
                      />
                    </div>

                    {/* Entity Type Selector */}
                    <div className="grid grid-cols-2 gap-4">
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, investorType: "Organization" })}
                        className={`py-4 text-[10px] font-black uppercase tracking-[0.2em] flex items-center justify-center gap-3 transition-all border ${formData.investorType === "Organization" ? "bg-brand/10 border-brand/50 text-brand" : "bg-black/50 border-white/5 text-zinc-500 hover:border-white/20 hover:text-white"}`}
                      >
                        <Building2 className="w-4 h-4" /> Institution
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, investorType: "Individual" })}
                        className={`py-4 text-[10px] font-black uppercase tracking-[0.2em] flex items-center justify-center gap-3 transition-all border ${formData.investorType === "Individual" ? "bg-brand/10 border-brand/50 text-brand" : "bg-black/50 border-white/5 text-zinc-500 hover:border-white/20 hover:text-white"}`}
                      >
                        <User className="w-4 h-4" /> Individual
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="text-[9px] font-black uppercase tracking-[0.2em] text-zinc-500">Representative Name</label>
                        <div className="relative">
                          <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
                          <input
                            type="text" required placeholder="Full Legal Name" className={`${inputClasses} pl-12 bg-black/50`}
                            value={formData.fullName} onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                          />
                        </div>
                      </div>

                      <AnimatePresence>
                        {formData.investorType === "Organization" && (
                          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="space-y-2 overflow-hidden">
                            <label className="text-[9px] font-black uppercase tracking-[0.2em] text-zinc-500">Corporate Entity</label>
                            <div className="relative">
                              <Briefcase className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
                              <input
                                type="text" required={formData.investorType === "Organization"} placeholder="Brand / Organization Name" className={`${inputClasses} pl-12 bg-black/50`}
                                value={formData.organization} onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                              />
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="text-[9px] font-black uppercase tracking-[0.2em] text-zinc-500">Corporate Email</label>
                        <div className="relative">
                          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
                          <input
                            type="email" required placeholder="Professional Email" className={`${inputClasses} pl-12 bg-black/50`}
                            value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="text-[9px] font-black uppercase tracking-[0.2em] text-zinc-500">Authorized Contact</label>
                        <div className="relative">
                          <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
                          <input
                            type="tel" required placeholder="Mobile Number" className={`${inputClasses} pl-12 bg-black/50`}
                            value={formData.mobile} onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-[9px] font-black uppercase tracking-[0.2em] text-zinc-500">Headquarters / Location</label>
                      <div className="relative">
                        <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
                        <input
                          type="text" required placeholder="City, Country / Primary Corporate Location" className={`${inputClasses} pl-12 bg-black/50`}
                          value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-[9px] font-black uppercase tracking-[0.2em] text-zinc-500">Strategic Intent</label>
                      <textarea
                        required
                        placeholder="Please describe your partnership interest, capital investment proposal, or sponsorship inquiry in detail."
                        rows={5}
                        className={`${inputClasses} resize-none bg-black/50 leading-relaxed`}
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      />
                    </div>

                    <div className="pt-4 border-t border-white/10">
                      <button
                        type="submit"
                        disabled={!isFormValid || status === "loading"}
                        className="w-full bg-brand hover:bg-brand-light py-5 text-[12px] font-black uppercase tracking-[0.3em] flex items-center justify-center gap-3 transition-all disabled:opacity-30 disabled:cursor-not-allowed group text-white"
                        style={{ clipPath: 'polygon(0 0, calc(100% - 15px) 0, 100% 15px, 100% 100%, 15px 100%, 0 calc(100% - 15px))' }}
                      >
                        {status === "loading" ? "ENCRYPTING TRANSMISSION..." : (
                          <>
                            TRANSMIT PROPOSAL <Send className="w-4 h-4 group-hover:translate-x-2 transition-transform" />
                          </>
                        )}
                      </button>
                    </div>

                    <AnimatePresence>
                      {status === "error" && (
                        <motion.div
                          initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }}
                          className="w-full bg-red-500/10 border border-red-500/20 py-3 text-center"
                        >
                          <p className="text-red-400 font-black text-[10px] uppercase tracking-widest px-4">{errorMessage}</p>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    <p className="text-[8px] text-zinc-500 text-center uppercase tracking-widest font-mono">
                      Your information is kept strictly private. Our partnerships team will be in touch.
                    </p>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
